import React, { useEffect, useState, useCallback } from "react";
import { api, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Send, Users, Loader2, Ticket, SlidersHorizontal, Mail } from "lucide-react";
import { relativeDate } from "@/lib/format";
import MessagesTabs from "@/components/MessagesTabs";
import { toast } from "sonner";

const GENDERS = [{ v: "all", l: "Tutti" }, { v: "uomo", l: "Uomo" }, { v: "donna", l: "Donna" }, { v: "altro", l: "Altro" }, { v: "na", l: "Preferisco non dirlo" }];
const AGES = ["<18", "18-24", "25-34", "35-44", "45-54", "55-64", "65+", "Non specificato"];
const emptySeg = () => ({ gender: "all", age_bucket: "all", city: "", country: "" });
const segToApi = (s) => ({ gender: s.gender === "all" ? "" : s.gender, age_bucket: s.age_bucket === "all" ? "" : s.age_bucket, city: s.city.trim(), country: s.country.trim() });

export default function VendorMessages() {
  const [sent, setSent] = useState([]);
  const [followers, setFollowers] = useState(0);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [seg, setSeg] = useState(emptySeg());
  const [preview, setPreview] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [couponId, setCouponId] = useState("");

  const load = useCallback(async () => {
    const [sm, bc, tpl] = await Promise.all([
      api.get("/vendor/sent", { headers: authHeaders("vendor") }),
      api.get("/vendor/broadcasts", { headers: authHeaders("vendor") }),
      api.get("/vendor/coupon-templates", { headers: authHeaders("vendor") }),
    ]);
    setSent(sm.data.sent || []);
    setFollowers(bc.data.followers || 0);
    setTemplates(tpl.data.templates || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const hasFilter = seg.gender !== "all" || seg.age_bucket !== "all" || seg.city.trim() || seg.country.trim();
    if (!hasFilter) { setPreview(null); return; }
    const t = setTimeout(() => {
      api.post("/vendor/segment/preview", { body: "x", ...segToApi(seg) }, { headers: authHeaders("vendor") })
        .then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [seg]);

  const recipientCount = preview ? preview.count : followers;
  const tplLabel = (t) => {
    const v = t.discount_value;
    if (t.discount_type === "percent") return `-${v}%`;
    if (t.discount_type === "fixed") return `-${v}€`;
    if (t.discount_type === "price") return `${v}€`;
    if (t.discount_type === "bogo") return "2x1";
    return "";
  };

  const sendBroadcast = async () => {
    if (!body.trim() && !couponId) return;
    setSending(true);
    try {
      const r = await api.post("/vendor/broadcast", { body: body.trim(), coupon_template_id: couponId || null, ...segToApi(seg) }, { headers: authHeaders("vendor") });
      toast.success(`Inviato a ${r.data.recipients} destinatari${r.data.coupon_title ? " con coupon 🎁" : ""} (${r.data.segment_label})`);
      setBody("");
      setCouponId("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6 kn-fade-up max-w-3xl" data-testid="vendor-messages-page">
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="text-2xl font-extrabold text-slate-900">Messaggi inviati</h1><p className="text-slate-500">Scrivi ai tuoi follower, anche a un pubblico mirato.</p></div>
        <div className="flex items-center gap-2 px-3 h-10 rounded-full bg-[#FFE4E6] text-[#BE123C] font-bold text-sm shrink-0"><Users className="h-4 w-4" /> {followers} follower</div>
      </div>
      <MessagesTabs />

      {/* Broadcast */}
      <div className="kn-card p-4 space-y-3">
        <div className="flex items-center gap-2 font-bold text-slate-800"><Send className="h-5 w-5 text-[#E63946]" /> Invia un messaggio ai follower</div>
        <Textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Es. Oggi aperitivo speciale dalle 18! Vi aspettiamo 🍹" className="rounded-xl" maxLength={1000} />

        {templates.length > 0 ? (
          <div className="space-y-1.5" data-testid="broadcast-coupon-picker">
            <Label className="text-xs flex items-center gap-1"><Ticket className="h-3.5 w-3.5 text-[#E63946]" /> Allega un coupon (facoltativo)</Label>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setCouponId("")} data-testid="broadcast-coupon-none"
                className={`px-3 h-9 rounded-full text-xs font-bold border transition-colors ${!couponId ? "kn-gradient text-white border-transparent" : "bg-white text-slate-600 border-slate-200"}`}>Nessuno</button>
              {templates.map((t) => (
                <button key={t.id} type="button" onClick={() => setCouponId(t.id)} data-testid={`broadcast-coupon-${t.id}`}
                  className={`px-3 h-9 rounded-full text-xs font-bold border inline-flex items-center gap-1.5 transition-colors ${couponId === t.id ? "kn-gradient text-white border-transparent" : "bg-white text-slate-600 border-slate-200"}`}>
                  <Ticket className="h-3.5 w-3.5" /> {t.title} <span className="opacity-80">{tplLabel(t)}</span>
                </button>
              ))}
            </div>
            {couponId && <p className="text-[11px] text-slate-500">Ogni destinatario riceverà il proprio coupon personale allegato al messaggio.</p>}
          </div>
        ) : (
          <p className="text-xs text-slate-400 flex items-center gap-1"><Ticket className="h-3.5 w-3.5" /> Crea dei coupon predefiniti nella sezione Coupon per poterli allegare qui.</p>
        )}

        <button type="button" onClick={() => setShowFilters((v) => !v)} data-testid="toggle-filters"
          className="flex items-center gap-2 text-sm font-bold text-[#E63946]">
          <SlidersHorizontal className="h-4 w-4" /> Scegli i destinatari {showFilters ? "▲" : "▼"}
        </button>
        {showFilters && (
          <div className="rounded-xl border border-slate-100 p-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="segment-filters">
            <div className="space-y-1.5">
              <Label className="text-xs">Genere</Label>
              <Select value={seg.gender} onValueChange={(v) => setSeg((s) => ({ ...s, gender: v }))}>
                <SelectTrigger className="h-10 rounded-xl" data-testid="seg-gender"><SelectValue /></SelectTrigger>
                <SelectContent>{GENDERS.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Età</Label>
              <Select value={seg.age_bucket} onValueChange={(v) => setSeg((s) => ({ ...s, age_bucket: v }))}>
                <SelectTrigger className="h-10 rounded-xl" data-testid="seg-age"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutte</SelectItem>{AGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Città</Label>
              <Input value={seg.city} onChange={(e) => setSeg((s) => ({ ...s, city: e.target.value }))} placeholder="Es. Pesaro" className="h-10 rounded-xl" data-testid="seg-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Paese</Label>
              <Input value={seg.country} onChange={(e) => setSeg((s) => ({ ...s, country: e.target.value }))} placeholder="Es. Italia" className="h-10 rounded-xl" data-testid="seg-country" />
            </div>
            <button type="button" onClick={() => setSeg(emptySeg())} className="text-xs text-slate-400 font-semibold text-left sm:col-span-2">Azzera filtri</button>
          </div>
        )}

        <Button onClick={sendBroadcast} disabled={sending || (!body.trim() && !couponId)} data-testid="broadcast-send" className="h-11 rounded-xl font-bold gap-2">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia a {recipientCount} {preview ? (recipientCount === 1 ? "destinatario" : "destinatari") : "follower"}
        </Button>
        {preview && <p className="text-xs text-slate-500" data-testid="segment-preview">Segmento <b>{preview.label}</b>: {preview.count} {preview.count === 1 ? "destinatario" : "destinatari"}</p>}
      </div>

      {/* Sent history */}
      <div className="kn-card p-4 space-y-3" data-testid="sent-history">
        <div className="flex items-center gap-2 font-bold text-slate-800"><Mail className="h-5 w-5 text-[#E63946]" /> Messaggi inviati</div>
        {sent.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">Non hai ancora inviato messaggi.</p>
        ) : (
          <div className="space-y-2">
            {sent.map((m) => (
              <div key={m.sent_id} className="bg-slate-50 rounded-xl p-3" data-testid={`sent-${m.sent_id}`}>
                <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{m.body}</p>
                <div className="flex items-center flex-wrap gap-2 mt-1.5">
                  <span className={`text-[11px] font-bold px-2 h-5 rounded-full inline-flex items-center ${m.kind === "direct" ? "bg-blue-100 text-blue-700" : m.kind === "segment" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {m.kind === "direct" ? "Diretto" : m.kind === "segment" ? "Segmentato" : "Tutti"}
                  </span>
                  <span className="text-[11px] text-slate-500">{m.segment_label}</span>
                  <span className="text-[11px] text-slate-400">· {m.recipients} destinatari · {relativeDate(m.created_at)}</span>
                  {m.coupon_title && <span className="text-[11px] text-[#BE123C] font-semibold inline-flex items-center gap-1"><Ticket className="h-3 w-3" /> {m.coupon_title}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
